import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { runInAction } from "mobx";
import { observer } from "mobx-react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import {
  Button,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  styled,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import { DateTime } from "luxon";

import { MONTH_OPTIONS } from "@config/constants";
import type { UtfTheme } from "@config/utilifeed.theme";

export const YearAndMonthSelect = styled("div", {
  shouldForwardProp: (prop) => prop !== "color",
})<{ color: string }>(({ theme, color }) => ({
  "& .MuiButton-root": {
    // Target the Button component's root class
    "&&": {
      ...theme.typography.button,
      lineHeight: 1,
      border:
        color === "white"
          ? `1px solid ${theme.palette.grey["50"]} !important`
          : `1px solid ${theme.palette[color]?.main} !important`, // Use optional chaining for palette[color]

      color:
        color === "white"
          ? `${theme.palette.grey["50"]} !important`
          : `${theme.palette[color]?.main} !important`, // Use optional chaining for palette[color]
      justifyContent: "space-between",
      padding: "5px 10px",
      textTransform: "capitalize",
      "&:disabled": {
        borderColor: theme.palette.grey["400"],
        color: `${theme.palette.grey["400"]} !important`,
      },
      "&:hover": {
        border: `1px solid ${theme.palette.primary.main}`,
      },
    },
  },

  "& .MuiMenuItem-root": {
    // Target the MenuItem component's root class
    textTransform: "capitalize !important",
  },

  "& .Mui-selected": {
    // Target the selected class on MenuItem
    background: `${theme.palette.action.selected} !important`, // Use theme.palette.action.selected
  },
}));

function SelectMonth(props) {
  const {
    store,
    name,
    classes: classesProp, // Keep for potential external class overrides
    classesOverride = {}, // Keep for potential external class overrides
    disabled,
    onChange,
    selectedYear, // you'll be able to select future months if this props does not has value
    options: userOptions,
    disableAllOption,
    color = "primary",
    translateNs = ["_common"],
    onMonthChange,
    ...rest
  } = props;
  const { t } = useTranslation([...translateNs]);
  const [selectedIndex, setSelected] = useState(() => store[name] ?? null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const anchorRef = useRef();
  const theme = useTheme(); // Use useTheme from @mui/material

  const options = Array.isArray(userOptions)
    ? new Map(userOptions)
    : (userOptions ?? new Map(MONTH_OPTIONS));

  if (disableAllOption) {
    options.delete(null);
  }

  const toggleMenuOpen = useCallback(() => {
    setIsMenuOpen((current) => !current);
  }, []);

  const toggleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleSelectItem = (_, index) => {
    setSelected(index);
    if (typeof onChange === "function") {
      onChange(index, name);
    }

    if (store?.hasOwnProperty(name)) {
      runInAction(() => {
        store[name] = index;
      });
    }
    setIsMenuOpen(false);
  };

  let monthName;

  if (
    DateTime.now().year === selectedYear &&
    DateTime.now().month < 12 &&
    (selectedIndex === undefined || selectedIndex === null)
  ) {
    monthName = MONTH_OPTIONS.get(String(store.to_month));
    setSelected(String(store.to_month));
    // Need this function to update the data library store when "All Year" option is disabled
    onMonthChange?.(String(store.to_month));
  } else {
    monthName = t(options.get(selectedIndex));
  }

  if (monthName === "") monthName = MONTH_OPTIONS.get(String(store.to_month));

  const currentMonth = DateTime.now().month;
  const currentYear = DateTime.now().year;

  return (
    <YearAndMonthSelect color={color}>
      {" "}
      {/* Pass color to styled component */}
      <Button
        ref={anchorRef}
        onClick={toggleMenuOpen}
        {...rest}
        className={clsx(classesProp, classesOverride)} // Keep original classNames for external styles
        disabled={disabled ?? store.is_data_processed === false}
        endIcon={isMenuOpen ? <ArrowDropUp /> : <ArrowDropDownIcon />}
        data-testid="select-month-button"
      >
        {monthName && t(monthName)}
      </Button>
      <Popper
        open={isMenuOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        sx={{ zIndex: theme.zIndex.drawer + 1 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={toggleMenuClose}>
                <MenuList data-testid="select-month-list">
                  {Array.from(options.entries()).map(([month, label]) => {
                    const isDisabled =
                      selectedYear === currentYear && (+month >= currentMonth || !month);
                    return (
                      <MenuItem
                        key={month}
                        selected={month === selectedIndex}
                        onClick={(event) => handleSelectItem(event, month)}
                        data-testid={`month-option-${month}`}
                        disabled={isDisabled}
                      >
                        {t(label)}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </YearAndMonthSelect>
  );
}

export default observer(SelectMonth);

export const classes = {};
